import { inject, Injectable } from '@angular/core';
import { ApiService } from '../api/api.service';
import { UserProfileService } from '../user/user-profile.service';
import {
  BehaviorSubject,
  Observable,
  concatMap,
  map,
  tap,
  throwError,
} from 'rxjs';
import { IPagination, ITag } from '../../types/contactModule';
import { template } from 'lodash';
import { HttpHeaders, HttpResponse } from '@angular/common/http';
import {
  IEngagementMessage,
  IMessageTemplate,
  ISenderID,
} from './types/broadcastTypes';
import { SecurePersistStateService } from '../storage/local-storage.service';
import { SettingsService } from '../settings/settings.service';
import { extractMessageAttributes } from '../../../utils/utils';
import { IUserOrganization } from '../../types/settingsDataModule';

// response from the recipient-batch upload endpoint (Proposal 1: reference-by-id)
interface IRecipientBatchResponse {
  batchId: string;
  count: number;
  expiresAt: string;
}

interface IBroadcastMessageBody {
  phoneNumbers?: string[];
  contacts?: string[];
  tags?: string[];
  organizations?: string[];
  segments?: string[];
  senderID: string;
  messageSubject: string;
  template?: string;
  messageBody: string;
  messageType: 'Instant' | 'Scheduled';
  saveContacts?: boolean;
  saveToExistingTags?: boolean;
  phoneNumberSelectedTags?: string[]; //selected tags for the phone numbers we want to save
  timeZone?: string | null;
  date?: string | null;
  time?: string | null;
  /**
   * The schedule as an ISO instant, already resolved in `timeZone`. When set
   * it is sent as `scheduleDate` instead of reading `date` + `time` in the
   * browser's own zone. Optional: callers that leave it out are unchanged.
   */
  scheduleInstant?: string | null;
  newTag?: {
    name: string;
    description: string;
  };
}

/** Audience ids for the recipient-count preview. */
export interface IRecipientCountPayload {
  phoneNumbers?: string[];
  contactIds?: string[];
  tagIds?: string[];
  organizationIds?: string[];
  segmentIds?: string[];
}

/** One page of message templates from the search endpoint. */
export interface ITemplatePage {
  content: IMessageTemplate[];
  totalElements: number;
  totalPages: number;
  currentPage: number;
  pageSize: number;
}

@Injectable({
  providedIn: 'root',
})
export class BroadcastMessageService {
  constructor() {
    // Subscribe to user institution data to get serviceAccountId
    this.settingsService.getUserInstitutionData.subscribe((data: IUserOrganization | null) => {
      if (data?.attributes?.serviceAccountId) {
        this.serviceAccountId = data.attributes.serviceAccountId;
      }
    });
  }

  get organizationId() {
    return this.userService.organizationId;
  }

  serviceName: string = 'blackkonnectcxpengagement'; //name of the API service being used.
  /** Segments live in the contacts service, not engagement (see ContactsService.serviceName). */
  private readonly contactsServiceName = 'blackkonnectcxpcontacts';

  // services
  apiService = inject(ApiService);
  userService = inject(UserProfileService);
  storageService = inject(SecurePersistStateService);
  settingsService = inject(SettingsService);

  // UI Manipulators
  isFetching = false; // used for GET requests
  isLoading = false; //used for POST,PUT etc requests

  // feedback messages
  private ErrorMsg = '';
  private successMsg = '';

  // timeout IDs for cleanup
  private successTimeoutId?: ReturnType<typeof setTimeout>;
  private errorTimeoutId?: ReturnType<typeof setTimeout>;

  // service account ID from user institution data
  private serviceAccountId: string | null = null;

  // Above this many phone numbers we pre-upload them to a recipient batch and send
  // only the returned id with the message, so the create-message payload stays small
  // and never trips the server's JSON body-size limit (Proposal 1: reference-by-id).
  private static readonly RECIPIENT_BATCH_THRESHOLD = 5000;

  // TABLE VALUES
  // messages data values - Active Messages | Inactive Messages | Templates | Archived Messages ------------------------------------------------------------
  private activeMessages = new BehaviorSubject<IEngagementMessage[]>([]);
  private inActiveMessages = new BehaviorSubject<IEngagementMessage[]>([]);
  private templates = new BehaviorSubject<IMessageTemplate[]>([]);
  private archivedMessages = new BehaviorSubject<IEngagementMessage[]>([]);

  // sender IDs
  private senderIDs = new BehaviorSubject<ISenderID[]>([]);
  /** Whose sender IDs the subject holds; another sign-in must not see them. */
  private senderIDsOrg: string | null = null;

  // DETAILED INFO
  // detailed message
  private detailedMessage = new BehaviorSubject<IEngagementMessage | null>(
    null
  );
  // detailed template
  private detailedTemplate = new BehaviorSubject<IMessageTemplate | null>(null);

  // PAGINATIONS
  // active messages pagination
  activeMessagesPagination = new BehaviorSubject<IPagination>({
    totalElements: 0,
    pageSize: 10,
    totalPages: 1,
    pageNumber: 0,
    offSet: 0,
  });

  // inactive messages
  inActiveMessagesPagination = new BehaviorSubject<IPagination>({
    totalElements: 0,
    pageSize: 10,
    totalPages: 1,
    pageNumber: 0,
    offSet: 0,
  });

  // templates pagination
  templatesPagination = new BehaviorSubject<IPagination>({
    totalElements: 0,
    pageSize: 10,
    totalPages: 1,
    pageNumber: 0,
    offSet: 0,
  });

  // archived messages pagination
  archivedMessagesPagination = new BehaviorSubject<IPagination>({
    totalElements: 0,
    pageSize: 10,
    totalPages: 1,
    pageNumber: 0,
    offSet: 0,
  });

  // utility functions
  private updateSuccessMsg(msg: string) {
    // Clear existing timeout before setting a new one
    if (this.successTimeoutId) {
      clearTimeout(this.successTimeoutId);
    }
    this.successMsg = msg;
    this.successTimeoutId = setTimeout(() => {
      this.successMsg = '';
      this.successTimeoutId = undefined;
    }, 5000);
  }

  private updateError(msg: string) {
    // Clear existing timeout before setting a new one
    if (this.errorTimeoutId) {
      clearTimeout(this.errorTimeoutId);
    }
    this.ErrorMsg = msg;
    this.errorTimeoutId = setTimeout(() => {
      this.ErrorMsg = '';
      this.errorTimeoutId = undefined;
    }, 5000);
  }

  // Helper method to create headers with X-ServiceAccount-ID
  private getHeadersWithServiceAccountId(): HttpHeaders | undefined {
    if (this.serviceAccountId) {
      return new HttpHeaders({
        'X-ServiceAccount-ID': this.serviceAccountId
      });
    }
    return undefined;
  }

  private isScheduledMessage(messageType: IBroadcastMessageBody['messageType']): boolean {
    return messageType?.toLowerCase() === 'scheduled';
  }

  private buildScheduleInstant(date?: string | null, time?: string | null): string | null {
    if (!date || !time) {
      return null;
    }
    const normalizedTime = time.length === 5 ? `${time}:00` : time;
    const dt = new Date(`${date}T${normalizedTime}`);
    return Number.isNaN(dt.getTime()) ? null : dt.toISOString();
  }

  // API CALLS
  sendMessage(messageBody: IBroadcastMessageBody): Observable<any> {
    this.isLoading = true;

    const phoneNumbers = messageBody.phoneNumbers ?? [];

    const successMessage = messageBody.saveContacts
      ? 'Contact Created'
      : 'Message sent successfully';

    // Large lists: pre-upload the numbers to a recipient batch and reference it by id,
    // keeping the create-message payload tiny (Proposal 1: reference-by-id). Otherwise
    // send the numbers inline as before.
    const useBatch =
      phoneNumbers.length > BroadcastMessageService.RECIPIENT_BATCH_THRESHOLD;

    const request$ = useBatch
      ? this.uploadRecipientBatch(phoneNumbers).pipe(
          concatMap((batchId) =>
            this.postEngagementMessage(
              this.buildMessagePayload(messageBody, { recipientBatchId: batchId })
            )
          )
        )
      : this.postEngagementMessage(this.buildMessagePayload(messageBody));

    return request$.pipe(
      tap({
        next: () => {
          this.updateSuccessMsg(successMessage);
          this.isLoading = false;
        },
        error: (err) => {
          this.isLoading = false;
          this.updateError(err?.message || 'Failed');
        },
        complete: () => {
          this.isLoading = false;
        },
      })
    );
  }

  /**
   * Builds the engagement-message payload shared by every send variant. When a
   * pre-uploaded recipient batch is referenced, the phone numbers are sent as
   * `recipients.recipientBatchId` instead of inline in `recipients.phoneNumbers`.
   */
  private buildMessagePayload(
    messageBody: IBroadcastMessageBody,
    opts: { recipientBatchId?: string } = {}
  ): Record<string, any> {
    const isScheduled = this.isScheduledMessage(messageBody.messageType);
    const scheduleInstant =
      messageBody.scheduleInstant ||
      this.buildScheduleInstant(messageBody.date, messageBody.time);

    const usingBatch = !!opts.recipientBatchId;

    // Contact attributes embedded as `{{attribute}}` placeholders in the body
    const attributes = extractMessageAttributes(messageBody.messageBody);

    const payload: Record<string, any> = {
      message: messageBody.messageBody,
      senderId: messageBody.senderID, //change this when you have available sender IDs
      hasAttribute: attributes.length > 0,
      attributes,
      saveContacts: !!messageBody.saveContacts,
      recipients: {
        // referencing a batch means the numbers are not sent inline
        phoneNumbers: usingBatch ? [] : messageBody.phoneNumbers,
        contactIds: messageBody.contacts,
        tagIds: messageBody.tags,
        organizationIds: messageBody.organizations,
        segmentIds: messageBody.segments,
        ...(usingBatch ? { recipientBatchId: opts.recipientBatchId } : {}),
      },
      template: messageBody.template,
      subject: messageBody.messageSubject,
      type: messageBody.messageType,
      scheduleTimeZone: isScheduled ? messageBody.timeZone || '' : '',
      scheduleDate: isScheduled && scheduleInstant ? scheduleInstant : '',
      scheduleTime: isScheduled ? messageBody.time || '' : '',
      options: {
        priority: 0,
        allowDuplicates: false,
        timezone: messageBody.timeZone,
        scheduleTime: isScheduled ? scheduleInstant : null,
      },
    };

    // when saving the numbers, attach the tag info (existing tag or one made on the go)
    if (messageBody.saveContacts) {
      payload['saveToExistingTags'] = !!messageBody.saveToExistingTags;
      //these are the tags the created phone numbers will be assigned to
      payload['phoneNumberSelectedTags'] = messageBody.phoneNumberSelectedTags;
      if (!messageBody.saveToExistingTags) {
        // no existing tag selected, so a new tag is created on the go to hold the numbers
        payload['newTag'] = {
          name: messageBody.newTag?.name,
          description: messageBody.newTag?.description,
        };
      }
    }

    return payload;
  }

  private postEngagementMessage(payload: Record<string, any>): Observable<any> {
    return this.apiService.post(
      `${this.serviceName}/api/v1/organization/${this.organizationId}/engagement-messages`,
      payload,
      this.getHeadersWithServiceAccountId()
    );
  }

  /**
   * Pre-uploads a large phone-number list to the dedicated recipient-batch endpoint
   * and resolves to the returned batch id. This endpoint has no message-payload size
   * limit, so the full list goes in a single request. The batch is org-scoped and
   * expires server-side (24h), so it is only referenced by the immediate send.
   */
  private uploadRecipientBatch(phoneNumbers: string[]): Observable<string> {
    return this.apiService
      .post<IRecipientBatchResponse>(
        `${this.serviceName}/api/v1/organization/${this.organizationId}/recipient-batches`,
        { phoneNumbers },
        this.getHeadersWithServiceAccountId()
      )
      .pipe(map((res) => res.batchId));
  }

  // send test message
  sendTestMessage(payload: {
    message: string;
    senderId: string;
    testContactId: string;
  }): Observable<any> {
    this.isLoading = true;

    return this.apiService
      .post(
        `${this.serviceName}/api/organization/${this.organizationId}/message-test-contacts/send-test-message`,
        payload
      )
      .pipe(
        tap({
          next: () => {
            this.updateSuccessMsg('Test message sent successfully');
            this.isLoading = false;
          },
          error: (err) => {
            this.isLoading = false;
            this.updateError(err?.message || 'Failed to send test message');
          },
          complete: () => {
            this.isLoading = false;
          },
        })
      );
  }

  // fetch message recipients count
  fetchMessageRecipientsCount(payload: {
    phoneNumbers?: string[];
    contactIds?: string[];
    tagIds?: string[];
    organizationIds?: string[];
    segmentIds?: string[];
  }): Observable<any> {
    this.isLoading = true;

    return this.apiService
      .post(
        `${this.serviceName}/api/organization/${this.organizationId}/engagement-messages/preview/recipient-count`,
        payload
      )
      .pipe(
        tap({
          next: () => {
            this.isLoading = false;
          },
          error: (err) => {
            this.isLoading = false;
            this.updateError(err?.message || 'Failed to fetch recipient count');
          },
          complete: () => {
            this.isLoading = false;
          },
        })
      );
  }

  // fetch campaign message recipients count
  fetchCampaignMessageRecipientCount(payload: {
    phoneNumbers?: string[];
    contactIds?: string[];
    tagIds?: string[];
    organizationIds?: string[];
    segmentIds?: string[];
  }): Observable<any> {
    this.isLoading = true;

    return this.apiService
      .post(
        `${this.serviceName}/api/engagement-campaigns/organisations/${this.organizationId}/preview/recipient-count`,
        payload
      )
      .pipe(
        tap({
          next: () => {
            this.isLoading = false;
          },
          error: (err) => {
            this.isLoading = false;
            this.updateError(err?.message || 'Failed to fetch campaign recipient count');
          },
          complete: () => {
            this.isLoading = false;
          },
        })
      );
  }

  // fetch active messages
  fetchActiveMessages(params?: Record<string, any>) {
    this.isFetching = true;
    const paramLength = Object.keys(params || {}).length;

    this.apiService
      .get<HttpResponse<any>>(
        `${this.serviceName}/api/v2/person-entities/organisations/${this.organizationId}/contacts`,
        paramLength
          ? params
          : {
              size: this.activeMessagesPagination.value.pageSize,
              page: this.activeMessagesPagination.value.pageNumber,
            } // Pass query parameters here
      )
      .subscribe({
        next: (data: any) => {
          this.activeMessages.next(data.items);
          this.activeMessagesPagination.next(data.itemPagination);
        },
        error: (err) => {
          this.isFetching = false;
          this.updateError(err.message);
          return throwError(() => err);
        },
        complete: () => {
          this.isFetching = false;
        },
      });
  }

  get getSuccessMsg() {
    return this.successMsg;
  }

  get getErrorMsag() {
    return this.ErrorMsg;
  }

  get getIsLoading() {
    return this.isLoading;
  }

  get getIsFetching() {
    return this.isFetching;
  }

  // fetch sender IDs
  // fetchSenderIDs() {
  //   this.isFetching = true;

  //   return this.apiService
  //     .get<ISenderID[]>(
  //       `${this.serviceName}/api/v1/sender-ids`
  //     )
  //     .pipe(
  //       tap({
  //         next: (data: ISenderID[]) => {
  //           this.senderIDs.next(data);
  //           this.isFetching = false;
  //         },
  //         error: (err) => {
  //           this.isFetching = false;
  //           this.updateError(err?.message || 'Failed to fetch sender IDs');
  //           return throwError(() => err);
  //         },
  //         complete: () => {
  //           this.isFetching = false;
  //         },
  //       })
  //     );
  // }

  // fetch sender IDs by organization
  fetchSenderIDs() {
    this.isFetching = true;
    const senderIdOrgId = this.userService.jwtOrgId || this.organizationId;
    if (this.senderIDsOrg !== String(senderIdOrgId)) {
      this.senderIDs.next([]);
      this.senderIDsOrg = String(senderIdOrgId);
    }

    return this.apiService
      .get<ISenderID[]>(
        `${this.serviceName}/api/v1/sender-ids/organisations/${senderIdOrgId}`
      )
      .pipe(
        tap({
          next: (data: ISenderID[]) => {
            this.senderIDs.next(data);
            this.isFetching = false;
          },
          error: (err) => {
            this.isFetching = false;
            this.updateError(err?.message || 'Failed to fetch sender IDs');
            return throwError(() => err);
          },
          complete: () => {
            this.isFetching = false;
          },
        })
      );
  }

  // getter for sender IDs observable
  get getSenderIDs$(): Observable<ISenderID[]> {
    return this.senderIDs.asObservable();
  }

  // ── Side-effect-free reads for the compose and template screens ──
  // None of these touch isLoading, isFetching or the feedback messages, so a
  // live count or a search never puts a spinner on the send button.

  /** Deduplicated audience size for a draft (POST preview/recipient-count). */
  countRecipients(payload: IRecipientCountPayload): Observable<number> {
    return this.apiService
      .post<any>(
        `${this.serviceName}/api/organization/${this.organizationId}/engagement-messages/preview/recipient-count`,
        payload
      )
      .pipe(
        map((response) => {
          const total =
            typeof response === 'number' ? response : response?.totalRecipients;
          return Number(total) || 0;
        })
      );
  }

  /**
   * One page of templates, searched on the server. `template_name` is a
   * contains-match; `sortBy` accepts nameAsc or nameDesc.
   */
  searchTemplates(params: {
    page?: number;
    size?: number;
    template_name?: string;
    sortBy?: 'nameAsc' | 'nameDesc';
  }): Observable<ITemplatePage> {
    const query: Record<string, string> = {
      page: String(params.page ?? 0),
      size: String(params.size ?? 10),
    };
    const term = (params.template_name || '').trim();
    if (term) query['template_name'] = term;
    if (params.sortBy) query['sortBy'] = params.sortBy;

    return this.apiService
      .get<any>(
        `${this.serviceName}/api/message-templates/organizations/${this.organizationId}`,
        query
      )
      .pipe(
        map((data) => {
          const content: IMessageTemplate[] = Array.isArray(data?.content)
            ? data.content
            : Array.isArray(data?.items)
              ? data.items
              : [];
          return {
            content,
            totalElements: Number(data?.totalElements ?? content.length) || 0,
            totalPages: Math.max(1, Number(data?.totalPages ?? 1) || 1),
            currentPage: Number(data?.currentPage ?? params.page ?? 0) || 0,
            pageSize: Number(data?.pageSize ?? params.size ?? 10) || 10,
          };
        })
      );
  }

  /** One page of segments for the recipient picker. No shared list state. */
  searchSegments(
    params: Record<string, any>
  ): Observable<{ items: any[]; totalElements: number; totalPages: number }> {
    return this.apiService
      .get<any>(
        `${this.contactsServiceName}/api/entity-segments/organisations/${this.organizationId}`,
        params
      )
      .pipe(
        map((data) => {
          const items: any[] = Array.isArray(data?.items) ? data.items : Array.isArray(data) ? data : [];
          const pagination = data?.itemPagination ?? {};
          return {
            items,
            totalElements: Number(pagination.totalElements ?? items.length) || 0,
            totalPages: Math.max(1, Number(pagination.totalPages ?? 1) || 1),
          };
        })
      );
  }

  /** Segments by uuid, to name recipients handed to compose in a link. */
  lookupSegments(uuids: string[]): Observable<any[]> {
    return this.apiService
      .get<any>(
        `${this.contactsServiceName}/api/entity-segments/organisations/${this.organizationId}`,
        { 'uuid.in': uuids, size: Math.max(uuids.length, 1) }
      )
      .pipe(
        map((data) =>
          Array.isArray(data) ? data : Array.isArray(data?.items) ? data.items : []
        )
      );
  }
}
